import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { getSupabaseClient } from './supabase';

/**
 * A record's logo (customers, venues): one image in a public storage bucket, described by five
 * `logo_*` columns on the record's table. Both buckets take PNG, JPEG or WebP up to 5 MB, and only
 * internal staff may write them (storage policies in the client-logos and venue_logo migrations).
 */
export type RecordLogoTarget = {
  bucket: 'client-logos' | 'venue-logos';
  table: 'clients' | 'venues';
};

export type RecordLogoFields = {
  logo_storage_path: string | null;
  logo_public_url: string | null;
  logo_alt_text: string | null;
  logo_uploaded_at: string | null;
  logo_uploaded_by: string | null;
};

export const RECORD_LOGO_MAX_BYTES = 5 * 1024 * 1024;
export const RECORD_LOGO_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
const LOGO_COLUMNS = 'logo_storage_path,logo_public_url,logo_alt_text,logo_uploaded_at,logo_uploaded_by';

export function validateRecordLogoFile(file: File): string | null {
  if (!RECORD_LOGO_ALLOWED_TYPES.includes(file.type as (typeof RECORD_LOGO_ALLOWED_TYPES)[number])) {
    return 'Choose a PNG, JPEG, or WebP image.';
  }
  if (file.size > RECORD_LOGO_MAX_BYTES) {
    return 'Choose an image smaller than 5 MB.';
  }
  return null;
}

/**
 * Uploads the file, points the record at it, then deletes the previous file. A failed record update
 * removes the new file; a failed cleanup is reported but leaves the new logo live.
 */
export async function uploadRecordLogo(params: RecordLogoTarget & {
  recordId: string;
  /** For the alt text, "<name> logo"; `fallbackName` stands in when it is blank. */
  displayName: string;
  fallbackName: string;
  file: File;
  previousStoragePath?: string | null;
  uploadedByProfileId?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  data: RecordLogoFields | null;
  error: StorageError | PostgrestError | Error | null;
  cleanupError: StorageError | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const validationError = validateRecordLogoFile(params.file);
  if (validationError) {
    return {
      data: null,
      error: new Error(validationError),
      cleanupError: null,
    };
  }

  const extension = params.file.type === 'image/png'
    ? 'png'
    : params.file.type === 'image/webp'
      ? 'webp'
      : 'jpg';
  const storagePath = `${params.recordId}/logo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${extension}`;
  const { error: uploadError } = await supabase.storage
    .from(params.bucket)
    .upload(storagePath, params.file, {
      cacheControl: '3600',
      upsert: false,
      contentType: params.file.type,
    });
  if (uploadError) return { data: null, error: uploadError, cleanupError: null };

  const { data: publicUrlData } = supabase.storage.from(params.bucket).getPublicUrl(storagePath);
  const payload: RecordLogoFields = {
    logo_storage_path: storagePath,
    logo_public_url: publicUrlData.publicUrl,
    logo_alt_text: `${params.displayName.trim() || params.fallbackName} logo`,
    logo_uploaded_at: new Date().toISOString(),
    logo_uploaded_by: params.uploadedByProfileId ?? null,
  };
  const { data, error: updateError } = await supabase
    .from(params.table)
    .update(payload)
    .eq('id', params.recordId)
    .select(LOGO_COLUMNS)
    .maybeSingle();
  if (updateError) {
    await supabase.storage.from(params.bucket).remove([storagePath]);
    return { data: null, error: updateError, cleanupError: null };
  }

  let cleanupError: StorageError | null = null;
  if (params.previousStoragePath && params.previousStoragePath !== storagePath) {
    const cleanup = await supabase.storage.from(params.bucket).remove([params.previousStoragePath]);
    cleanupError = cleanup.error ?? null;
  }
  return {
    data: (data as RecordLogoFields | null) ?? payload,
    error: null,
    cleanupError,
  };
}

/** Clears the record's logo columns, then deletes the file. */
export async function removeRecordLogo(params: RecordLogoTarget & {
  recordId: string;
  storagePath?: string | null;
  supabase?: SupabaseClient;
}): Promise<{
  error: StorageError | PostgrestError | null;
  cleanupError: StorageError | null;
}> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from(params.table)
    .update({
      logo_storage_path: null,
      logo_public_url: null,
      logo_alt_text: null,
      logo_uploaded_at: null,
      logo_uploaded_by: null,
    })
    .eq('id', params.recordId);
  if (error) return { error, cleanupError: null };

  if (!params.storagePath) return { error: null, cleanupError: null };
  const cleanup = await supabase.storage.from(params.bucket).remove([params.storagePath]);
  return { error: null, cleanupError: cleanup.error ?? null };
}
